/* import __COLOCATED_TEMPLATE__ from './translations-table-details-row.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import * as d3 from 'd3';

export default class TranslationsTableDetailsRowComponent extends Component
{
	@service store;
	@service ajax;

	@tracked statisticsData = null;
	@tracked isLoadingStatistics = false;

	constructor() {
		super(...arguments);
		this.loadStatistics();
	}

	@action
	createIntermediate(passing)
	{	
		var ajax = this.ajax;
		var translation = this.args.translation;
		var activator = passing.ActivatorId;
		activator.get('EventId').then(function(event)
		{
			var timingPointId = activator.get('IntermediateTimingPointId');
			
			var bib = translation.bib;
		
			if(bib != null)
			{
				ajax.request("/events/"+event.id+"/intermediateTimingPoint/"+timingPointId+"/enterTime/"+(new Date(passing.time)).getTime()+"/bib/"+bib);
			}
		});
	};
	
	@action
	createFinishRecord(passing)
	{
		var ajax = this.ajax;
		var translation = this.args.translation;
		var activator = passing.ActivatorId;
		activator.get('EventId').then(function(event)
		{
			var timingPointId = activator.get('IntermediateTimingPointId');
			
			var bib = translation.bib;
		
			if(bib != null)
			{
				ajax.request("/events/"+event.id+"/enterFinish/"+bib+"/time/"+(new Date(passing.time)).getTime());
			}
		});
	};
	
	@action
	createPlungerRecord(passing)
	{
		var ajax = this.ajax;
		var translation = this.args.translation;
		var activator = passing.ActivatorId;
		activator.get('EventId').then(function(event)
		{
			var timingPointId = activator.get('IntermediateTimingPointId');
			
			var bib = translation.bib;
		
			if(bib != null)
			{
				ajax.request("/events/"+event.id+"/enterPlunger/"+bib+"/time/"+(new Date(passing.time)).getTime());
			}
		});
	};
	
	@action
	createBibRecord(passing)
	{
		var ajax = this.ajax;
		var translation = this.args.translation;
		var activator = passing.ActivatorId;
		activator.get('EventId').then(function(event)
		{
			var timingPointId = activator.get('IntermediateTimingPointId');
			
			var bib = translation.bib;
		
			if(bib != null)
			{
				ajax.request("/events/"+event.id+"/enterBibFinish/"+bib+"/time/"+(new Date(passing.time)).getTime());
			}
		});
	};
	
	@action
	createStartRecord(passing)
	{
		var ajax = this.ajax;
		var translation = this.args.translation;
		var activator = passing.ActivatorId;
		activator.get('EventId').then(function(event)
		{
			var timingPointId = activator.get('IntermediateTimingPointId');
	
			var bib = translation.bib;

			if(bib != null)
			{
				ajax.request("/events/"+event.id+"/enterStart/"+bib+"/time/"+(new Date(passing.time)).getTime());
			}
		});
	};

	async loadStatistics() {
		this.isLoadingStatistics = true;
		try {
			const chipId = this.args.translation.chip;
			const stats = await this.store.query('statistic', {
				'filter[chip]': chipId,
				'sort': 'time'
			});

			// Convert to array and filter for battery voltage data
			const statsArray = [...stats].filter(stat => stat.batteryVolts && stat.batteryVolts > 0);
			this.statisticsData = statsArray;

			// Schedule graph creation after render
			if (statsArray.length > 0) {
				setTimeout(() => this.createBatteryGraph(), 100);
			}
		} catch (error) {
			console.error('Error loading statistics:', error);
			this.statisticsData = [];
		} finally {
			this.isLoadingStatistics = false;
		}
	}

	@action
	createBatteryGraph() {
		const data = this.statisticsData;
		if (!data || data.length === 0) return;

		const containerId = `battery-graph-${this.args.translation.chip}`;
		const container = document.getElementById(containerId);
		if (!container) return;

		// Clear any existing SVG
		d3.select(container).selectAll('*').remove();

		// Set dimensions
		const margin = { top: 30, right: 30, bottom: 50, left: 60 };
		const width = 800 - margin.left - margin.right;
		const height = 200 - margin.top - margin.bottom;

		// Create SVG
		const svg = d3.select(container)
			.append('svg')
			.attr('width', width + margin.left + margin.right)
			.attr('height', height + margin.top + margin.bottom)
			.append('g')
			.attr('transform', `translate(${margin.left},${margin.top})`);

		// Prepare data - sort by time to ensure proper line drawing
		const chartData = data.map(d => ({
			time: new Date(d.time * 1000),
			voltage: d.batteryVolts
		})).sort((a, b) => a.time - b.time);

		// Get the full date range from the statistics
		const [minDate, maxDate] = d3.extent(chartData, d => d.time);

		// Create scales with exact date range from data
		const x = d3.scaleTime()
			.domain([minDate, maxDate])
			.range([0, width])
			.nice(); // Adds nice round values to the domain

		const y = d3.scaleLinear()
			.domain([
				Math.max(0, d3.min(chartData, d => d.voltage) - 0.5),
				d3.max(chartData, d => d.voltage) + 0.5
			])
			.range([height, 0]);

		// Create line generator
		const line = d3.line()
			.x(d => x(d.time))
			.y(d => y(d.voltage));

		// Determine appropriate time format based on date range
		const timeSpan = maxDate - minDate;
		const oneDay = 24 * 60 * 60 * 1000;
		const oneHour = 60 * 60 * 1000;

		let timeFormat;
		let tickCount;

		if (timeSpan < oneHour) {
			timeFormat = d3.timeFormat('%H:%M:%S');
			tickCount = 5;
		} else if (timeSpan < oneDay) {
			timeFormat = d3.timeFormat('%H:%M');
			tickCount = 6;
		} else if (timeSpan < 7 * oneDay) {
			timeFormat = d3.timeFormat('%m/%d %H:%M');
			tickCount = 5;
		} else {
			timeFormat = d3.timeFormat('%m/%d/%Y');
			tickCount = 5;
		}

		// Add X axis with appropriate formatting
		svg.append('g')
			.attr('transform', `translate(0,${height})`)
			.call(d3.axisBottom(x)
				.ticks(tickCount)
				.tickFormat(timeFormat))
			.selectAll('text')
			.style('text-anchor', 'end')
			.attr('dx', '-.8em')
			.attr('dy', '.15em')
			.attr('transform', 'rotate(-45)');

		// Add X axis label
		svg.append('text')
			.attr('x', width / 2)
			.attr('y', height + margin.bottom - 5)
			.style('text-anchor', 'middle')
			.text('Time');

		// Add Y axis
		svg.append('g')
			.call(d3.axisLeft(y));

		// Add Y axis label
		svg.append('text')
			.attr('transform', 'rotate(-90)')
			.attr('y', 0 - margin.left + 10)
			.attr('x', 0 - (height / 2))
			.attr('dy', '1em')
			.style('text-anchor', 'middle')
			.text('Battery Voltage (V)');

		// Add title with date range
		const dateRangeText = timeSpan < oneDay
			? `${d3.timeFormat('%m/%d/%Y')(minDate)}`
			: `${d3.timeFormat('%m/%d/%Y')(minDate)} - ${d3.timeFormat('%m/%d/%Y')(maxDate)}`;

		svg.append('text')
			.attr('x', width / 2)
			.attr('y', -10)
			.style('text-anchor', 'middle')
			.style('font-size', '12px')
			.style('font-weight', 'bold')
			.text(`Battery Voltage History (${dateRangeText})`);

		// Add the line
		svg.append('path')
			.datum(chartData)
			.attr('fill', 'none')
			.attr('stroke', 'steelblue')
			.attr('stroke-width', 2)
			.attr('d', line);

		// Add dots
		svg.selectAll('dot')
			.data(chartData)
			.enter()
			.append('circle')
			.attr('cx', d => x(d.time))
			.attr('cy', d => y(d.voltage))
			.attr('r', 3)
			.attr('fill', 'steelblue');
	}
};